import { useAtom } from '@reatom/npm-react'
import React, { memo, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Array, Order, pipe } from '@blade/std-lib'
import { NumberUtil } from '@root/shared'
import { PriceChartThumbnail } from '@root/shared/ui-kit'

import type { PairModel, PriceChartInterval } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'
import type { UTCTimestamp } from 'lightweight-charts'

const radius = () => 10

type Props = {
  pair: PairModel
  interval: PriceChartInterval
} & ClassNameProps

export const PairExploreItemChart: React.FC<Props> = memo(
  ({ pair, interval, className }) => {
    const holderRef = useRef<HTMLDivElement>(null)

    const [priceChart] = useAtom(
      match(interval)
        .with('minutes', () => pair.priceChartMinutes)
        .with('seconds', () => pair.priceChartSeconds)
        .exhaustive(),
    )

    const candles = useMemo(
      () =>
        pipe(
          priceChart,
          Array.map((x) => ({
            time: new Date(x.timestamp).getTime() as UTCTimestamp,
            open: NumberUtil.safeParseFloat(x.price.open) ?? 0,
            high: NumberUtil.safeParseFloat(x.price.high) ?? 0,
            low: NumberUtil.safeParseFloat(x.price.low) ?? 0,
            close: NumberUtil.safeParseFloat(x.price.close) ?? 0,
          })),
          Array.sortWith((x) => x.time, Order.Number),
          Array.uniqBy((x) => x.time),
        ),
      [priceChart],
    )

    return (
      <Holder ref={holderRef} className={className}>
        <Chart candles={candles} radius={radius} />
      </Holder>
    )
  },
)

const Holder = styled.div`
  width: 100%;
  height: 100%;
`

const Chart = styled(PriceChartThumbnail)``
